import React, { useState, useEffect } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { SubscriptionBanner } from '../components/SubscriptionBanner'
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Tooltip,
  Badge,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import {
  Menu as MenuIcon,
  Dashboard,
  Business,
  People,
  Devices,
  Input,
  DeviceHub,
  Rule,
  Analytics,
  Notifications,
  Send,
  Security,
  Receipt,
  History,
  ExpandLess,
  ExpandMore,
  ChevronLeft,
  Brightness4,
  Brightness7,
  SensorsOutlined,
  WarningAmber,
  AdminPanelSettings,
  Logout,
  Campaign,
} from '@mui/icons-material'
import { useAuthStore } from '../store/authStore'
import { usePermissions, PERMS } from '../hooks/usePermissions'
import { useThemeMode } from '../theme/ThemeProvider'
import { useTranslation } from 'react-i18next'
import { LANGUAGES, LanguageCode } from '../i18n'
import { useNotifications } from '../hooks/useNotifications'
import { analytics, EVENTS } from '../lib/analytics'
import { LocalShipping, Map as MapIcon, Link as LinkIcon, Visibility, Place, Assignment, Build, PowerSettingsNew, Language, Assessment, LocalGasStation } from '@mui/icons-material'

const DRAWER_WIDTH = 282

interface NavLeaf {
  label: string
  path: string
  icon: React.ReactNode
  /** If set, item is shown only when the user has at least one of these permissions. */
  perms?: string[]
  /** If set, item is shown only when the user has at least one of these roles. */
  roles?: string[]
}
interface NavItem {
  label: string
  icon: React.ReactNode
  path?: string
  perms?: string[]
  roles?: string[]
  excludeRoles?: string[]
  children?: NavLeaf[]
}

// label = i18n key under "nav." namespace
const NAV: NavItem[] = [
  // Role-specific home screens (mutually exclusive — hidden when user has a higher role)
  { label: 'nav.myDevices',    icon: <Visibility />,    path: '/viewer',   roles: ['viewer'], excludeRoles: ['admin', 'user', 'driver'] },
  { label: 'nav.driver',        icon: <LocalShipping />, path: '/driver',   roles: ['driver'], excludeRoles: ['admin', 'user'] },

  // Core — high-frequency features
  { label: 'nav.dashboard',     icon: <Dashboard />,     path: '/dashboard', roles: ['admin', 'user'] },
  { label: 'nav.liveMap',      icon: <MapIcon />,      path: '/map',          roles: ['admin', 'user', 'driver', 'viewer'] },
  { label: 'nav.tripPlayback', icon: <MapIcon />,      path: '/playback',     roles: ['admin', 'user', 'driver', 'viewer'] },
  { label: 'nav.trips',         icon: <MapIcon />,      path: '/trips',        roles: ['admin', 'user', 'driver', 'viewer'] },
  { label: 'nav.remoteControl',icon: <PowerSettingsNew />, path: '/commands', roles: ['admin'] },

  // Fleet management
  {
    label: 'nav.devices',
    icon: <Devices />,
    roles: ['admin', 'user', 'driver', 'viewer'],
    children: [
      { label: 'nav.allDevices',         path: '/devices',            icon: <Devices />,
        perms: [PERMS.VIEW_ALL_DEVICES, PERMS.VIEW_ASSIGNED_DEVICES] },
      { label: 'nav.sensorDefinitions',  path: '/sensor-definitions', icon: <SensorsOutlined />,
        perms: [PERMS.VIEW_SENSORS] },
      { label: 'nav.deviceGroups',       path: '/device-groups',      icon: <Devices />,
        perms: [PERMS.MANAGE_DEVICE_ASSIGNMENTS] },
      { label: 'nav.deviceAssignments',  path: '/device-assignments', icon: <LinkIcon />,
        perms: [PERMS.MANAGE_DEVICE_ASSIGNMENTS] },
    ],
  },
  { label: 'nav.geofences',     icon: <Place />,        path: '/geofences',    roles: ['admin', 'user'] },
  { label: 'nav.dispatch',      icon: <Assignment />,   path: '/jobs',         roles: ['admin', 'user'] },
  { label: 'nav.maintenance',   icon: <Build />,        path: '/maintenance',  roles: ['admin', 'user'] },
  { label: 'nav.fuelAnalytics', icon: <LocalGasStation />, path: '/fuel-analytics', roles: ['admin', 'user'] },
  { label: 'nav.digitalTwin',  icon: <DeviceHub />,    path: '/digital-twin', roles: ['admin', 'user'] },

  // Monitoring & intelligence
  {
    label: 'nav.ruleEngine',
    icon: <Rule />,
    roles: ['admin', 'user'],
    children: [
      { label: 'nav.rules',  path: '/rules',  icon: <Rule />,         perms: [PERMS.MANAGE_RULES] },
      { label: 'nav.alerts', path: '/alerts', icon: <WarningAmber />, perms: [PERMS.VIEW_ALERTS] },
    ],
  },
  { label: 'nav.notifications', icon: <Notifications />, path: '/notifications', roles: ['admin', 'user', 'driver', 'viewer'] },
  { label: 'nav.sendNotification', icon: <Send />, path: '/send-notification', roles: ['admin'] },
  { label: 'nav.audiences', icon: <Campaign />, path: '/audiences', roles: ['admin', 'user'] },
  { label: 'nav.analytics',     icon: <Analytics />,    path: '/analytics',    perms: [PERMS.VIEW_ANALYTICS] },
  { label: 'nav.reports',       icon: <Assessment />,   path: '/reports',      roles: ['admin', 'user'] },

  // Administration
  {
    label: 'nav.identity',
    icon: <People />,
    roles: ['admin'],
    children: [
      { label: 'nav.users', path: '/users', icon: <People />, perms: [PERMS.MANAGE_USERS] },
      { label: 'nav.roles', path: '/roles', icon: <AdminPanelSettings />, perms: [PERMS.MANAGE_ROLES] },
    ],
  },
  { label: 'nav.trustCircle',  icon: <Security />,     path: '/trust-circle', perms: [PERMS.MANAGE_TRUST_CIRCLES] },
  { label: 'nav.billing',       icon: <Receipt />,      path: '/billing',      perms: [PERMS.MANAGE_BILLING] },
  { label: 'nav.security',      icon: <Security />,     path: '/security' },
]

export function AppLayout() {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { mode, toggleMode, language, setLanguage } = useThemeMode()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout } = useAuthStore()
  const [langMenuAnchor, setLangMenuAnchor] = useState<null | HTMLElement>(null)
  const { data: notifications = [] } = useNotifications()
  const unreadCount = notifications.filter((n) => !n.read).length
  const { hasRole, hasPermission } = usePermissions()

  const pathIsActive = (path?: string) => Boolean(
    path && (location.pathname === path || location.pathname.startsWith(`${path}/`)),
  )

  // Filter NAV based on the current user's roles + permissions
  const visibleNav = NAV
    .map((item) => {
      const filteredChildren = item.children?.filter((c) =>
        (!c.perms || hasPermission(...c.perms)) && (!c.roles || hasRole(...c.roles)),
      )
      return { ...item, children: filteredChildren }
    })
    .filter((item) => {
      const passRole = !item.roles || hasRole(...item.roles)
      const passPerm = !item.perms || hasPermission(...item.perms)
      const passExclude = !item.excludeRoles || !hasRole(...item.excludeRoles)
      const hasVisibleChildren = !item.children || item.children.length > 0
      return passRole && passPerm && passExclude && hasVisibleChildren
    })

  const currentNav = visibleNav
    .flatMap<{ label: string; path: string }>((item) => item.children
      ? item.children.map((child) => ({ label: child.label, path: child.path }))
      : [{ label: item.label, path: item.path ?? '' }])
    .filter((item) => pathIsActive(item.path))
    .sort((a, b) => b.path.length - a.path.length)[0]

  const [drawerOpen, setDrawerOpen] = useState(!isMobile)
  const [openGroups, setOpenGroups] = useState<string[]>(['nav.identity', 'nav.devices', 'nav.ruleEngine'])
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  useEffect(() => {
    setDrawerOpen(!isMobile)
  }, [isMobile])

  useEffect(() => {
    const activeGroup = visibleNav.find((item) => item.children?.some((child) => pathIsActive(child.path)))
    if (activeGroup) {
      setOpenGroups((previous) => previous.includes(activeGroup.label)
        ? previous
        : [...previous, activeGroup.label])
    }
  }, [location.pathname])

  const toggleGroup = (label: string) => {
    setOpenGroups((prev) =>
      prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label],
    )
  }

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', color: '#D9E7F5' }}>
      <Toolbar sx={{ justifyContent: 'space-between', px: 2.25, minHeight: '74px !important' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 40, height: 40, display: 'grid', placeItems: 'center', borderRadius: 2.5, bgcolor: 'rgba(255,255,255,.08)', border: '1px solid rgba(255,255,255,.1)' }}>
            <Box component="img" src="/omnitrace_logo.png" alt="OmniTrace" sx={{ width: 30, height: 30 }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight={800} color="#fff" lineHeight={1.15}>OmniTrace</Typography>
            <Typography variant="caption" sx={{ color: '#7992AC', letterSpacing: '.08em', fontSize: 9 }}>FLEET OS</Typography>
          </Box>
        </Box>
        {!isMobile && (
          <IconButton size="small" onClick={() => setDrawerOpen(false)} sx={{ color: '#8FA6BD' }}>
            <ChevronLeft />
          </IconButton>
        )}
      </Toolbar>
      <Box sx={{ px: 2.25, pb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.25, borderRadius: 2.5, bgcolor: 'rgba(32,201,151,.08)', border: '1px solid rgba(32,201,151,.15)' }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#39D6A4', boxShadow: '0 0 0 5px rgba(57,214,164,.09)' }} />
          <Box>
            <Typography variant="caption" fontWeight={700} sx={{ color: '#DDF9F1', display: 'block', lineHeight: 1.2 }}>{t('common.systemsOperational')}</Typography>
            <Typography sx={{ color: '#7692AB', fontSize: 10 }}>{t('common.liveDataConnected')}</Typography>
          </Box>
        </Box>
      </Box>
      <Divider sx={{ borderColor: 'rgba(255,255,255,.07)' }} />
      <List sx={{ flex: 1, overflow: 'auto', px: 1.25, py: 1.5 }}>
        {visibleNav.map((item) => {
          if (item.children) {
            const isGroupActive = item.children.some((c) => pathIsActive(c.path))
            const isOpen = openGroups.includes(item.label)
            return (
              <React.Fragment key={item.label}>
                <ListItemButton
                  onClick={() => toggleGroup(item.label)}
                  selected={isGroupActive}
                  sx={{
                    borderRadius: 2.5, mb: 0.5, color: '#A9BDD0',
                    '& .MuiListItemIcon-root': { color: 'inherit' },
                    '&.Mui-selected': { color: '#fff', bgcolor: 'rgba(21,151,229,.16)' },
                    '&:hover': { bgcolor: 'rgba(255,255,255,.06)', color: '#fff' },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 38 }}>{item.icon}</ListItemIcon>
                  <ListItemText primary={t(item.label)} />
                  {isOpen ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <List disablePadding>
                    {item.children.map((child) => (
                      <ListItemButton
                        key={child.path}
                        selected={pathIsActive(child.path)}
                        onClick={() => {
                          navigate(child.path)
                          if (isMobile) setDrawerOpen(false)
                        }}
                        sx={{
                          pl: 5, borderRadius: 2.5, mb: 0.5, color: '#8199B0',
                          '& .MuiListItemIcon-root': { color: 'inherit' },
                          '&.Mui-selected': { color: '#fff', bgcolor: 'rgba(21,151,229,.18)', '&::before': { content: '""', width: 3, height: 22, borderRadius: 2, bgcolor: '#42B7F1', position: 'absolute', left: 7 } },
                          '&:hover': { bgcolor: 'rgba(255,255,255,.06)', color: '#fff' },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 34, '& svg': { fontSize: 18 } }}>
                          {child.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={t(child.label)}
                          primaryTypographyProps={{ fontSize: 14 }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              </React.Fragment>
            )
          }
          return (
            <ListItemButton
              key={item.path}
              selected={pathIsActive(item.path)}
              onClick={() => {
                navigate(item.path!)
                if (isMobile) setDrawerOpen(false)
              }}
              sx={{
                borderRadius: 2.5, mb: 0.5, color: '#A9BDD0',
                '& .MuiListItemIcon-root': { color: 'inherit' },
                '&.Mui-selected': {
                  color: '#fff',
                  background: 'linear-gradient(90deg, rgba(21,151,229,.24), rgba(21,151,229,.08))',
                  '&::before': { content: '""', width: 3, height: 24, borderRadius: 2, bgcolor: '#42B7F1', position: 'absolute', left: 7 },
                },
                '&:hover': { bgcolor: 'rgba(255,255,255,.06)', color: '#fff' },
              }}
            >
              <ListItemIcon sx={{ minWidth: 38 }}>{item.icon}</ListItemIcon>
              <ListItemText primary={t(item.label)} />
            </ListItemButton>
          )
        })}
      </List>
      <Divider sx={{ borderColor: 'rgba(255,255,255,.07)' }} />
      <Box sx={{ p: 2.25 }}>
        <Typography variant="caption" sx={{ color: '#6F879F' }}>
          {t('common.version')} · OmniTrace
        </Typography>
      </Box>
    </Box>
  )

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Box
        component="a"
        href="#main-content"
        sx={{
          position: 'fixed',
          zIndex: theme.zIndex.tooltip + 1,
          top: 10,
          insetInlineStart: 10,
          px: 2,
          py: 1,
          borderRadius: 2,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          fontWeight: 700,
          textDecoration: 'none',
          transform: 'translateY(-160%)',
          '&:focus': { transform: 'translateY(0)' },
        }}
      >
        {t('common.skipToContent')}
      </Box>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: theme.zIndex.drawer - 1,
          width: !isMobile && drawerOpen ? `calc(100% - ${DRAWER_WIDTH}px)` : '100%',
          insetInlineStart: !isMobile && drawerOpen ? DRAWER_WIDTH : 0,
          transition: theme.transitions.create(['width', 'inset-inline-start']),
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: mode === 'dark' ? 'rgba(7,17,31,.80)' : 'rgba(255,255,255,.82)',
          backdropFilter: 'blur(18px)',
          color: theme.palette.text.primary,
        }}
      >
        <Toolbar sx={{ minHeight: '72px !important', px: { xs: 1.5, sm: 3 } }}>
          <IconButton
            edge="start"
            onClick={() => setDrawerOpen((p) => !p)}
            aria-label={drawerOpen ? t('common.closeNavigation') : t('common.openNavigation')}
            aria-expanded={drawerOpen}
            sx={{ mr: { xs: 1, sm: 2 }, bgcolor: 'action.hover', borderRadius: 2.5 }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={800} noWrap>
              {currentNav ? t(currentNav.label) : 'OmniTrace'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' }, lineHeight: 1 }}>
              {t('dashboard.overview')}
            </Typography>
          </Box>

          {/* Language selector */}
          <Tooltip title={t('common.language')}>
            <IconButton aria-label={t('common.language')} onClick={(e) => setLangMenuAnchor(e.currentTarget)}>
              <Language />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={langMenuAnchor}
            open={Boolean(langMenuAnchor)}
            onClose={() => setLangMenuAnchor(null)}
          >
            {LANGUAGES.map((lang) => (
              <MenuItem
                key={lang.code}
                selected={language === lang.code}
                onClick={() => {
                  analytics.track(EVENTS.SETTINGS_LANGUAGE_CHANGED, { language: lang.code, label: lang.label })
                  setLanguage(lang.code as LanguageCode)
                  setLangMenuAnchor(null)
                }}
              >
                <Typography variant="body2" fontWeight={language === lang.code ? 700 : 400}>
                  {lang.label}
                </Typography>
              </MenuItem>
            ))}
          </Menu>

          <Tooltip title={mode === 'light' ? t('common.darkMode') : t('common.lightMode')}>
            <IconButton aria-label={mode === 'light' ? t('common.darkMode') : t('common.lightMode')} onClick={() => { analytics.track(EVENTS.SETTINGS_THEME_CHANGED, { theme: mode === 'light' ? 'dark' : 'light' }); toggleMode() }}>
              {mode === 'light' ? <Brightness4 /> : <Brightness7 />}
            </IconButton>
          </Tooltip>
          <Tooltip title={t('common.notifications')}>
            <IconButton aria-label={`${t('common.notifications')}${unreadCount ? ` (${unreadCount})` : ''}`} sx={{ mr: { xs: 0, sm: 1 } }} onClick={() => navigate('/notifications')}>
              <Badge badgeContent={unreadCount} color="error" max={99}>
                <Notifications />
              </Badge>
            </IconButton>
          </Tooltip>
          <Tooltip title={user?.name ?? 'Account'}>
            <IconButton aria-label={user?.name ?? 'Account'} onClick={(e) => setAnchorEl(e.currentTarget)}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 14 }}>
                {user?.name?.[0]?.toUpperCase() ?? 'A'}
              </Avatar>
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem disabled>
              <Box>
                <Typography variant="body2" fontWeight={600}>{user?.name}</Typography>
                <Typography variant="caption" color="text.secondary">{user?.email}</Typography>
              </Box>
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={() => {
                logout()
                navigate('/auth/login')
              }}
            >
              <ListItemIcon><Logout fontSize="small" /></ListItemIcon>
              {t('common.logout')}
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Drawer
        variant={isMobile ? 'temporary' : 'persistent'}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          width: drawerOpen ? DRAWER_WIDTH : 0,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: isMobile ? `min(${DRAWER_WIDTH}px, 88vw)` : DRAWER_WIDTH,
            boxSizing: 'border-box',
            background: 'linear-gradient(180deg, #091827 0%, #0A1C2F 100%)',
            borderRight: '1px solid rgba(255,255,255,.05)',
            transition: theme.transitions.create('width'),
          },
        }}
      >
        {drawerContent}
      </Drawer>

      <Box
        component="main"
        id="main-content"
        tabIndex={-1}
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3, xl: 4 },
          pt: { xs: 2.5, sm: 3 },
          mt: '72px',
          width: '100%',
          maxWidth: drawerOpen && !isMobile ? `calc(100vw - ${DRAWER_WIDTH}px)` : '100vw',
          minHeight: '100vh',
          transition: theme.transitions.create('margin'),
          bgcolor: 'background.default',
          overflowX: 'hidden',
        }}
      >
        <SubscriptionBanner />
        <Outlet />
      </Box>
    </Box>
  )
}
